import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  loadFuelEntries,
  loadFuelEntriesFailure,
  loadFuelEntriesSuccess,
  saveFuelEntries,
  saveFuelEntriesFailure,
  saveFuelEntriesSuccess,
} from './fuel.actions';
import { FuelService } from '../services/fuel.service';

@Injectable()
export class FuelEffects {
  constructor(
    private actions$: Actions,
    private fuelService: FuelService,
    private toastController: ToastController,
    private store: Store
  ) {}

  loadFuelEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFuelEntries),
      switchMap(({ vanId }) =>
        this.fuelService.getFuelData(vanId).pipe(
          map((data) => loadFuelEntriesSuccess({ data })),
          catchError((error) =>
            of(loadFuelEntriesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveFuelEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveFuelEntries),
      switchMap(({ vanId, entries }) =>
        this.fuelService.upsertFuelData(vanId, { entries }).pipe(
          map(() => saveFuelEntriesSuccess({ vanId })),
          catchError((error) =>
            of(saveFuelEntriesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveFuelEntriesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveFuelEntriesSuccess),
        tap(async () => {
          const toast = await this.toastController.create({
            message: 'Fuel entry saved successfully',
            duration: 2000,
            color: 'success',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );

  // Reload fuel entries after successful save
  reloadAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveFuelEntriesSuccess),
      map(({ vanId }) => loadFuelEntries({ vanId }))
    )
  );

  saveFuelEntriesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveFuelEntriesFailure),
        tap(async ({ error }) => {
          const toast = await this.toastController.create({
            message: `Failed to save fuel entry: ${error}`,
            duration: 3000,
            color: 'danger',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );
}
